'use client';

import type { Yarn } from '@/lib/types';
import { formatSkeinCount, uncountedPartialGrams } from '@/lib/skeinFormat';
import YarnTile from './YarnTile';
import YarnSwatch from './YarnSwatch';

type YarnStats = { inUse: number; available: number; rawAvailable: number };
type YarnSection = { key: string; label: string; yarns: Yarn[] };
type YarnGroupCardLabels = { yarn: string; yarnsPlural: string; available: string; inUse: string };

interface YarnGroupCardProps {
  section: YarnSection;
  getYarnStats: (yarn: Yarn) => YarnStats;
  onSelectYarn: (yarn: Yarn) => void;
  onDrillIn: () => void;
  labels: YarnGroupCardLabels;
}

const fontYarnDisplay = "var(--font-geist-sans), system-ui, -apple-system, sans-serif";

/**
 * YarnGroupCard — one card in the BY COLOR / BY BRAND / BY WEIGHT grouped views.
 *
 * Renders an aggregate bucket of yarns (the "WHITE" bucket, the "DROPS"
 * bucket, etc.): rich YarnTile on the left, section label + yarn-count
 * subline, big tabular skein number on the right (with +partial grams
 * when present), AVAILABLE mono caption, and a per-yarn round mini-swatch
 * row underneath.
 *
 * Restored at build 1436 after the 058b2db simplification dropped the
 * YarnTile + typography in favor of tiny dot swatches — Sebastian
 * flagged the regression on Telegram 2026-05-18.
 *
 * ─────────────────────────────────────────────────────────────────────
 * VISUAL TWIN: src/components/YarnRowCard.tsx (used in the BY ALPHABET
 * view).
 *
 * The two cards render different domain objects — this one shows an
 * aggregate bucket of yarns; the twin shows a single yarn (or yarn-name
 * colorway group) — but they SHARE the same visual recipe: YarnTile +
 * bold uppercase title + mono subline + big tabular skein number +
 * AVAILABLE caption + bottom swatch row. Coherence is maintained by
 * both files using the same primitives (`YarnTile`, `Chip`,
 * `YarnSwatch`, `formatSkeinCount`, the `--paper-2` / `--ink` tokens)
 * — NOT by inheritance or composition.
 *
 * If you change layout, padding, font, or token usage here, change
 * YarnRowCard.tsx the same way in the same commit — or explicitly
 * document the intentional divergence in both files. The 058b2db
 * regression that necessitated build 1436's restore happened because
 * the two files drifted silently; the docblock pairing exists to make
 * that drift visible to the next editor before it ships.
 *
 * Why not one unified component? Two domain compositions sharing a
 * visual recipe via shared primitives is the correct pattern at this
 * scale — investigated and chosen 2026-05-18 (task #16). A slot-based
 * unifier would relocate domain logic without simplifying it.
 * ─────────────────────────────────────────────────────────────────────
 */
export function YarnGroupCard({
  section,
  getYarnStats,
  onSelectYarn,
  onDrillIn,
  labels,
}: YarnGroupCardProps) {
  const stats = section.yarns.map((y) => getYarnStats(y));
  const totalInUse = stats.reduce((sum, x) => sum + x.inUse, 0);
  const totalAvailable = stats.reduce((sum, x) => sum + x.available, 0);
  const totalPartialGrams = section.yarns.reduce((sum, y) => sum + uncountedPartialGrams(y), 0);
  const anyOver = stats.some((x) => x.rawAvailable < 0);
  const borderColor = anyOver ? 'var(--over)' : 'var(--ink)';
  const numberColor = anyOver ? 'var(--over-num)' : 'var(--ink)';
  const yarnCount = section.yarns.length;

  return (
    <div
      style={{
        width: '100%',
        background: 'var(--paper-2)',
        border: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 88,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onDrillIn}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onDrillIn();
          }
        }}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'stretch',
          flex: 1,
          color: 'inherit',
        }}
      >
        <YarnTile yarns={section.yarns} borderColor={borderColor} />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: '8px 12px 10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                data-testid="yarn-bucket-label"
                style={{
                  fontFamily: fontYarnDisplay,
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  textTransform: 'uppercase',
                  lineHeight: 0.95,
                  color: 'var(--ink)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {section.label}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  letterSpacing: '0.12em',
                  color: 'var(--ink-2)',
                  marginTop: 3,
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {yarnCount} {yarnCount === 1 ? labels.yarn : labels.yarnsPlural}
              </div>
            </div>
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                <span
                  style={{
                    fontFamily: fontYarnDisplay,
                    fontWeight: 800,
                    fontSize: 18,
                    letterSpacing: '-0.02em',
                    color: numberColor,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1,
                  }}
                >
                  {formatSkeinCount(totalAvailable, section.yarns[0]?.gramsPerSkein)}
                </span>
                {totalPartialGrams > 0 && (
                  <span
                    style={{
                      fontFamily: fontYarnDisplay,
                      fontWeight: 800,
                      fontSize: 13,
                      color: numberColor,
                      lineHeight: 1,
                    }}
                  >
                    +{totalPartialGrams}g
                  </span>
                )}
              </div>
              <span
                data-testid="yarn-bucket-available"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 6.5,
                  letterSpacing: '0.18em',
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  marginTop: 2,
                }}
              >
                {labels.available}
              </span>
              {totalInUse > 0 && (
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 6.5,
                    letterSpacing: '0.18em',
                    color: 'var(--ink-3)',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                    marginTop: 2,
                  }}
                >
                  {formatSkeinCount(totalInUse, section.yarns[0]?.gramsPerSkein)} {labels.inUse}
                </span>
              )}
            </div>
          </div>
          {/* Per-yarn round mini swatches — one per yarn in this bucket.
              Click drills into the individual yarn (bypasses bucket drill-in).
              Reported missing 2026-05-18 by Veronika in Telegram (msg 3925)
              after the build-1436 grouped-card restore — the swatch row
              existed in the pre-refactor inline implementation but the
              extraction at 058b2db dropped it. */}
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
            onClick={(e) => e.stopPropagation()}
          >
            {section.yarns.map((c) => {
              const cStats = getYarnStats(c);
              const over = cStats.rawAvailable < 0;
              return (
                <button
                  key={c.id ?? c.uuid}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectYarn(c);
                  }}
                  aria-label={`${c.colorName || 'colorway'}${c.colorNumber ? ` #${c.colorNumber}` : ''}`}
                  title={`${c.colorName || ''}${c.colorNumber ? ` #${c.colorNumber}` : ''}`}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: over ? '1px solid var(--over)' : 'none',
                    padding: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <YarnSwatch yarn={c} style={{ width: '100%', height: '100%' }} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default YarnGroupCard;
